// The developer zone's drawings: one small scene per "why" block, the path's folder-to-Banger line and the creator
// icons. Static markup in the zone's colours (style.ts sets them through CSS variables), never text: every word on
// the page stays in the copy. Parts carry classes the zone's scroll-in animation moves (`.in` is the end state).

const NS = "http://www.w3.org/2000/svg";

function svg(viewBox: string, cls: string, body: string): SVGSVGElement {
  const s = document.createElementNS(NS, "svg");
  s.setAttribute("viewBox", viewBox);
  s.setAttribute("class", cls);
  s.setAttribute("aria-hidden", "true");
  s.setAttribute("focusable", "false");
  s.innerHTML = body;
  return s;
}

const heart = (x: number, y: number, i: number) =>
  `<g transform="translate(${x} ${y})"><path class="v-rise" style="--i:${i}" d="M0 4c0-3 4-5 6-1 2-4 6-2 6 1 0 4-6 8-6 8s-6-4-6-8z"/></g>`;

const SCENES: Record<string, string> = {
  // a row of library tiles; the middle one is marked updated and hearts rise from it
  updates: `
    <rect class="v-tile" x="22" y="42" width="56" height="74" rx="8"/>
    <rect class="v-tile" x="162" y="42" width="56" height="74" rx="8"/>
    <rect class="v-tile hot" x="88" y="30" width="64" height="86" rx="9"/>
    <circle class="v-pop accent" cx="150" cy="32" r="7"/>
    <g class="accent">${heart(104, 14, 0)}${heart(126, 4, 1)}${heart(140, 20, 2)}</g>`,
  // one big play button, friends already in
  friends: `
    <circle class="v-ring" cx="150" cy="70" r="40"/>
    <circle class="v-pop accent" cx="150" cy="70" r="30"/>
    <path class="v-play" d="M142 56l22 14-22 14z"/>
    <g class="v-slide"><circle class="v-face" cx="58" cy="54" r="16"/><circle class="v-face" cx="80" cy="84" r="16"/><circle class="v-face" cx="48" cy="98" r="14"/>
    <circle class="accent" cx="70" cy="44" r="4"/><circle class="accent" cx="92" cy="74" r="4"/></g>
    <path class="v-draw" d="M96 84c12-4 18-10 24-14"/>`,
  // a server stack and the numbers it gives back
  hosting: `
    <rect class="v-tile" x="30" y="30" width="84" height="22" rx="5"/><rect class="v-tile" x="30" y="58" width="84" height="22" rx="5"/><rect class="v-tile" x="30" y="86" width="84" height="22" rx="5"/>
    <circle class="accent" cx="44" cy="41" r="3"/><circle class="accent" cx="44" cy="69" r="3"/><circle class="accent" cx="44" cy="97" r="3"/>
    <g class="accent"><rect class="v-grow" x="140" y="76" width="14" height="32" rx="3"/><rect class="v-grow" style="--i:1" x="162" y="58" width="14" height="50" rx="3"/><rect class="v-grow" style="--i:2" x="184" y="36" width="14" height="72" rx="3"/></g>`,
  // a bell for the waitlist and a clip with its play button
  before: `
    <path class="v-tile" d="M58 96V70a22 22 0 0144 0v26l8 8H50z"/><circle class="accent" cx="80" cy="112" r="6"/>
    <path class="v-draw" d="M42 58c-6 8-6 22 0 30M118 58c6 8 6 22 0 30"/>
    <rect class="v-tile hot" x="136" y="40" width="80" height="60" rx="8"/>
    <path class="v-play" d="M168 58l18 12-18 12z"/>
    <path class="v-draw accent-stroke" d="M140 112h72"/>`,
  // the creator's share of a sale
  cost: `
    <circle class="v-ring" cx="120" cy="72" r="44" stroke-width="18"/>
    <circle class="v-arc" cx="120" cy="72" r="44" stroke-width="18" pathLength="100" stroke-dasharray="80 100" transform="rotate(-90 120 72)"/>
    <circle class="v-pop accent" cx="120" cy="72" r="10"/>`,
};

/** The scene for a "why" block, by its copy id; null for a block with none. */
export function whyScene(id: string | undefined): SVGSVGElement | null {
  const body = id ? SCENES[id] : undefined;
  return body ? svg("0 0 240 140", "why-scene", body) : null;
}

/** The path's picture: a folder, a line drawing itself, and Banger's mark at the end (an <img> the caller adds). */
export function pathLine(): SVGSVGElement {
  return svg(
    "0 0 320 80",
    "path-line",
    `<path class="v-tile" d="M8 22h30l8 8h46v38H8z"/><path class="v-dash" d="M108 49h176" pathLength="100"/><path class="accent" d="M282 41l14 8-14 8z"/>`,
  );
}

const ICONS: string[] = [
  // claim: a flag
  `<path class="v-stroke" d="M10 36V6"/><path class="accent" d="M10 7h20l-5 7 5 7H10z"/>`,
  // hear from players: two bubbles
  `<path class="v-stroke" d="M6 8h20v14H14l-6 5v-5H6z"/><path class="accent" d="M18 18h16v12h-2v5l-5-5h-9z"/>`,
  // post what's new: a note with lines
  `<rect class="v-stroke" x="8" y="5" width="24" height="30" rx="3"/><path class="accent-stroke" d="M13 13h14M13 19h14M13 25h9"/>`,
  // publish assets: a cube
  `<path class="v-stroke" d="M20 4l14 8v16l-14 8-14-8V12z"/><path class="accent" d="M20 20l14-8v16l-14 8z"/>`,
];

/** The icon for the creator list's nth item (CREATOR_GETS order). */
export function getIcon(i: number): SVGSVGElement | null {
  return ICONS[i] ? svg("0 0 40 40", "get-icon", ICONS[i]!) : null;
}
